import React, {FC, useEffect} from 'react';
import {useForm} from 'react-hook-form';
import {Teacher, TeacherRoleNameMap, TeacherUpdateRequest} from '../../shared/types/teacher';
import {updateTeacherFetch} from '../../app/axios/teacherFetches';
import HLFormInput from '../../shared/components/input/HLFormInput';
import HLButton, {HLButtonStyleType} from '../../shared/components/button/HLButton';
import HLErrorMessage from '../../shared/components/error-message/HLErrorMessage';
import HLModal from '../../shared/components/modal/HLModal';
import {processFormServerError} from '../../shared/utils/processFormServerError';
import HLFormSelect from "../../shared/components/select/HLFormSelect";

const roleOptions = Object.entries(TeacherRoleNameMap).map(([value, label]) => ({
    value,
    label
}));


interface UpdateTeacherModalProps {
    open: boolean;
    setOpen: (value: boolean) => void;
    teacher: Teacher | null;
    onSuccess: () => void;
}

const UpdateTeacherModal: FC<UpdateTeacherModalProps> = (
    {
        open,
        setOpen,
        teacher,
        onSuccess
    }) => {
    const {
        register,
        handleSubmit,
        reset,
        setError,
        setValue,
        watch,
        formState: {errors, isSubmitting}
    } = useForm<TeacherUpdateRequest>()

    useEffect(() => {
        if (teacher && open) {
            setValue('firstName', teacher.firstName);
            setValue('lastName', teacher.lastName);
            setValue('role', teacher.role)
        }
    }, [teacher, open, setValue]);

    const updateTeacher = async (request: TeacherUpdateRequest) => {
        if (!teacher) return;

        try {
            if (!request.password) request.password = null
            await updateTeacherFetch(teacher.id, request);
            closeModal();
            onSuccess()
        } catch (err: any) {
            processFormServerError(err, setError);
        }
    };

    const closeModal = () => {
        reset()
        setOpen(false)
    };

    if (!teacher) return null;

    return (
        <HLModal open={open} onClose={closeModal} title="Изменить учителя">
            <form onSubmit={handleSubmit(updateTeacher)} className="columnContainer">
                <HLErrorMessage error={errors.root?.message}/>

                <HLFormInput
                    id="firstName"
                    {...register('firstName', {
                        required: 'Имя обязательно'
                    })}
                    placeholder="Имя"
                    error={errors.firstName?.message}
                />

                <HLFormInput
                    id="lastName"
                    {...register('lastName', {
                        required: 'Фамилия обязательна'
                    })}
                    placeholder="Фамилия"
                    error={errors.lastName?.message}
                />

                <HLFormInput
                    id="password"
                    type="password"
                    {...register('password', {
                        minLength: {value: 8, message: 'Минимум 8 символов'}
                    })}
                    placeholder="Новый пароль (необязательно)"
                    error={errors.password?.message}
                />

                <HLFormSelect
                    id="role"
                    {...register('role', {required: 'Роль обязательна'})}
                    value={watch('role') || ''}
                    options={[
                        {value: '', label: 'Роль'},
                        ...roleOptions
                    ]}
                    error={errors.role?.message}
                />

                <div className="inlineContainer">
                    <HLButton
                        type="submit"
                        disabled={isSubmitting}
                    >
                        Изменить
                    </HLButton>
                    <HLButton
                        type="button"
                        onClick={closeModal}
                        styleType={HLButtonStyleType.GRAY}
                    >
                        Отмена
                    </HLButton>
                </div>
            </form>
        </HLModal>
    );
};

export default UpdateTeacherModal; 